import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { useAuth, API } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { SealCheck, Lock, ArrowRight } from '@phosphor-icons/react';

/** Public landing for an Architect invite. Handles all 4 states:
 *  - not-signed-in → CTA to sign in (returns to this URL after OAuth)
 *  - already-architect → "You're an Architect already" toast
 *  - invite-valid + signed-in → confirmation modal + claim button
 *  - invite-invalid/claimed/revoked → error message
 */
export default function InviteClaim() {
    const { code } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const ent = useEntitlements();
    const [claiming, setClaiming] = useState(false);
    const [claimed, setClaimed] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Persist intent across the OAuth redirect round-trip.
        if (code) {
            try { sessionStorage.setItem('defectr:pending-invite', code); } catch { /* noop */ }
        }
    }, [code]);

    // Auto-consume if the user just completed OAuth and had a pending invite.
    useEffect(() => {
        if (authLoading || !user || !code || claimed || ent?.tier === 'architect') return;
        let cancelled = false;
        (async () => {
            setClaiming(true);
            try {
                await axios.post(`${API}/invites/${encodeURIComponent(code)}/claim`, {}, { withCredentials: true });
                if (cancelled) return;
                try { sessionStorage.removeItem('defectr:pending-invite'); } catch { /* noop */ }
                setClaimed(true);
                confetti({ particleCount: 120, spread: 80, origin: { y: 0.3 } });
                toast.success('Welcome, Architect 🏛️ — everything\'s on you, forever.');
                await ent?.refresh?.();
            } catch (err) {
                if (cancelled) return;
                const status = err?.response?.status;
                if (status === 404) setError('This invite link doesn\'t exist.');
                else if (status === 409) setError('This invite has already been claimed.');
                else if (status === 410) setError('This invite has been revoked.');
                else setError('Could not claim invite. Please try again.');
            } finally {
                if (!cancelled) setClaiming(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, code, authLoading]);

    if (claimed || ent?.tier === 'architect') {
        return (
            <Shell>
                <SealCheck size={48} weight="fill" color="#1FA5CF" className="mx-auto" />
                <h1 className="font-display text-3xl font-bold mt-3">You&apos;re an Architect</h1>
                <p className="text-sm text-[#52525B] mt-2">Unlimited everything, forever, on the house.</p>
                <button
                    onClick={() => navigate('/app')}
                    data-testid="invite-claim-go-app-btn"
                    className="mt-6 inline-flex items-center gap-2 bg-[#1FA5CF] text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                >
                    Open DefectR <ArrowRight size={14} weight="bold" />
                </button>
            </Shell>
        );
    }

    if (error) {
        return (
            <Shell>
                <Lock size={40} weight="duotone" color="#52525B" className="mx-auto" />
                <h1 className="font-display text-2xl font-bold mt-3">Invite unavailable</h1>
                <p className="text-sm text-[#52525B] mt-2" data-testid="invite-error">{error}</p>
                <a href="/" className="mt-6 inline-block underline decoration-[#1FA5CF] underline-offset-4 font-mono text-xs uppercase tracking-[0.2em]">
                    Return to DefectR
                </a>
            </Shell>
        );
    }

    if (authLoading) return <Shell><p>Loading…</p></Shell>;

    if (!user) {
        return (
            <Shell>
                <SealCheck size={48} weight="duotone" color="#1FA5CF" className="mx-auto" />
                <h1 className="font-display text-3xl font-bold mt-3">You&apos;ve been invited</h1>
                <p className="text-sm text-[#52525B] mt-2">
                    Architect access — lifetime, unlimited, free. Sign in to claim.
                </p>
                <a
                    href={(typeof window !== 'undefined') ? `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/invite/' + code)}` : '#'}
                    data-testid="invite-sign-in-btn"
                    className="mt-6 inline-flex items-center gap-2 bg-[#18181B] text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#09090B] active:scale-95 transition-all duration-150"
                >
                    Continue with Google <ArrowRight size={14} weight="bold" />
                </a>
                <p className="text-[10px] text-[#52525B] font-mono uppercase tracking-[0.18em] mt-4">
                    Invite code · {code}
                </p>
            </Shell>
        );
    }

    return (
        <Shell>
            <SealCheck size={48} weight="duotone" color="#1FA5CF" className="mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-3">Claim your Architect access</h1>
            <p className="text-sm text-[#52525B] mt-2">
                This single-use link grants you unlimited properties, defects, floorplans and PDFs — forever, at no cost.
            </p>
            <button
                disabled={claiming}
                onClick={() => { /* the useEffect auto-consumed already; this is a fallback */ }}
                data-testid="invite-claiming"
                className={`mt-6 inline-flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] ${claiming ? 'bg-[#A1A1AA] text-white' : 'bg-[#1FA5CF] text-white'}`}
            >
                {claiming ? 'Claiming…' : 'Claiming…'}
            </button>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="border-b border-[#18181B]">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
                    <img
                        src="/defectr-logo.png"
                        alt="DefectR"
                        className="h-9 w-auto object-contain"
                    />
                </div>
            </header>
            <main className="flex-1 flex items-center justify-center px-6">
                <div className="max-w-md w-full text-center border border-[#18181B] p-8" data-testid="invite-claim-shell">
                    {children}
                </div>
            </main>
        </div>
    );
}
